import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	CampaignRoutingCondition,
	CampaignRoutingField,
	CampaignRoutingOperator,
} from "@signalist/shared-types";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";

import {
	CAMPAIGN_ROUTING_FIELD_GROUPS,
	fieldLabelKey,
	isScoringField,
	operatorLabelKey,
	operatorsFor,
	ROUTING_I18N,
	withField,
	withOperator,
} from "./utils/campaignRoutingFields";
import { RoutingValuePicker } from "./RoutingValuePicker";

interface RoutingConditionRowProps {
	listId: number;
	condition: CampaignRoutingCondition;
	onChange: (condition: CampaignRoutingCondition) => void;
	onRemove?: () => void;
}

/** One condition of a rule: field, operator (only those the field takes) and its values. */
export const RoutingConditionRow: FunctionComponent<RoutingConditionRowProps> = ({
	listId,
	condition,
	onChange,
	onRemove,
}) => {
	const { t } = useTranslation();

	// Each group's first field carries the group's heading (Lead, Company, Lead scoring; mockup frame 3).
	const fieldOptions = useMemo(
		() =>
			CAMPAIGN_ROUTING_FIELD_GROUPS.flatMap((group) =>
				group.fields.map((field, index) => ({
					id: field,
					title: t(fieldLabelKey(field)),
					groupHeading: index === 0 ? t(group.labelKey) : undefined,
				}))
			),
		[t]
	);
	const operatorOptions = useMemo(
		() =>
			operatorsFor(condition.field).map((operator) => ({
				id: operator,
				title: t(operatorLabelKey(operator)),
			})),
		[condition.field, t]
	);

	return (
		<div className="flex flex-col gap-1" data-testid="routing-condition">
			<div className="flex flex-wrap items-center gap-2">
				{/* Uncontrolled (defaultValue): a controlled Dropdown replays its handler (web §16). */}
				<Dropdown
					floating
					portal
					data={fieldOptions}
					defaultValue={fieldOptions.find((option) => option.id === condition.field)}
					handleSelect={(item) => onChange(withField(condition, item.id as CampaignRoutingField))}
					testId="routing-field"
				/>
				<Dropdown
					floating
					portal
					key={condition.field}
					data={operatorOptions}
					defaultValue={operatorOptions.find((option) => option.id === condition.operator)}
					handleSelect={(item) =>
						onChange(withOperator(condition, item.id as CampaignRoutingOperator))
					}
					testId="routing-operator"
				/>
				<div className="min-w-[200px] flex-1">
					<RoutingValuePicker
						key={`${condition.field}:${condition.operator}`}
						condition={condition}
						listId={listId}
						onChange={(update) => onChange({ ...condition, ...update })}
					/>
				</div>
				{onRemove && (
					<button
						aria-label={t(`${ROUTING_I18N}.editor.removeCondition`)}
						className="shrink-0 cursor-pointer rounded p-1 text-text-meta hover:bg-background-light"
						data-testid="remove-condition"
						type="button"
						onClick={onRemove}
					>
						<Icon className="h-4 w-4" icon={faXmark} />
					</button>
				)}
			</div>
			{isScoringField(condition.field) && (
				<p className="text-xs text-text-meta" data-testid="routing-scoring-hint">
					{t(`${ROUTING_I18N}.editor.scoringHint`)}
				</p>
			)}
		</div>
	);
};
